// Role-Permission Matrix - Single source of truth for access control

export const ROLES = {
  ADMIN: 'admin',
  FREE_USER: 'free_user',
  PARTNER: 'partner',
  INDIVIDUAL_AGENT: 'individual_agent',
};

export const ROLE_CONFIG = {
  [ROLES.ADMIN]: {
    label: 'iMoney Admin',
    color: '#C62828',
    defaultRoute: '/admin',
    sidebarSections: ['main_admin', 'admin'],
    features: [
      'manage_users', 'configure_commission', 'configure_rewards',
      'manage_products', 'approve_partners', 'manage_marketing',
      'manage_referrals', 'view_analytics',
    ],
  },
  [ROLES.FREE_USER]: {
    label: 'Member',
    color: '#00B894',
    defaultRoute: '/dashboard',
    sidebarSections: ['main', 'applications', 'referral', 'rewards'],
    features: [
      'apply_products', 'invite_friends', 'earn_tokens',
      'redeem_rewards', 'referral_levels', 'view_applications',
      'share_referral',
    ],
  },
  [ROLES.PARTNER]: {
    label: 'Partner',
    color: '#1B365D',
    defaultRoute: '/partner',
    sidebarSections: ['main_partner', 'partner'],
    features: [
      'submit_applications', 'ocr_scan', 'manage_downline',
      'view_commission', 'view_applications', 'download_materials',
    ],
  },
  [ROLES.INDIVIDUAL_AGENT]: {
    label: 'Agent',
    color: '#7B1FA2',
    defaultRoute: '/agent',
    sidebarSections: ['agent_view'],
    features: [
      'view_products', 'view_commission_table', 'submit_applications',
      'view_sales', 'download_materials', 'ocr_scan',
    ],
  },
};

// Route access mapping
const ROUTE_ACCESS = {
  // Public (any authenticated user)
  '/products': [ROLES.ADMIN, ROLES.FREE_USER, ROLES.PARTNER, ROLES.INDIVIDUAL_AGENT],
  '/notifications': [ROLES.ADMIN, ROLES.FREE_USER, ROLES.PARTNER, ROLES.INDIVIDUAL_AGENT],
  '/profile': [ROLES.ADMIN, ROLES.FREE_USER, ROLES.PARTNER, ROLES.INDIVIDUAL_AGENT],

  // Free User routes
  '/dashboard': [ROLES.FREE_USER],
  '/apply': [ROLES.FREE_USER],
  '/my-applications': [ROLES.FREE_USER],
  '/rewards': [ROLES.FREE_USER],
  '/redemption': [ROLES.FREE_USER],
  '/referral-history': [ROLES.FREE_USER],
  '/referral-links': [ROLES.FREE_USER],
  '/invite': [ROLES.FREE_USER],
  '/join-us': [ROLES.FREE_USER],

  // Partner routes
  '/partner': [ROLES.PARTNER],
  '/partner/submit': [ROLES.PARTNER],
  '/partner/downline': [ROLES.PARTNER],
  '/partner/commission': [ROLES.PARTNER],
  '/partner/marketing': [ROLES.PARTNER, ROLES.INDIVIDUAL_AGENT, ROLES.FREE_USER, ROLES.ADMIN],
  '/partner/applications': [ROLES.PARTNER],

  // Individual Agent routes
  '/commission-table': [ROLES.INDIVIDUAL_AGENT],
  '/agent': [ROLES.INDIVIDUAL_AGENT],
  '/agent/submit': [ROLES.INDIVIDUAL_AGENT],
  '/agent/sales': [ROLES.INDIVIDUAL_AGENT],

  // Admin routes
  '/admin': [ROLES.ADMIN],
  '/admin/referrals': [ROLES.ADMIN],
  '/admin/partners': [ROLES.ADMIN],
  '/admin/reward-config': [ROLES.ADMIN],
  '/admin/product-config': [ROLES.ADMIN],
  '/admin/marketing': [ROLES.ADMIN],
  '/admin/users': [ROLES.ADMIN],
  '/admin/sales-listing': [ROLES.ADMIN],
  '/admin/campaign-settings': [ROLES.ADMIN],
};

export function canAccessRoute(role, path) {
  // Check exact match first
  if (ROUTE_ACCESS[path]) {
    return ROUTE_ACCESS[path].includes(role);
  }
  // Check prefix match for nested routes
  const matchingRoute = Object.keys(ROUTE_ACCESS).find(
    (route) => path.startsWith(route + '/') || path === route
  );
  if (matchingRoute) {
    return ROUTE_ACCESS[matchingRoute].includes(role);
  }
  return false;
}

export function getSidebarSections(role) {
  return ROLE_CONFIG[role]?.sidebarSections || [];
}

export function hasFeature(role, featureName) {
  return ROLE_CONFIG[role]?.features?.includes(featureName) || false;
}

export function getDefaultRoute(role) {
  return ROLE_CONFIG[role]?.defaultRoute || '/login';
}

export function getRoleLabel(role) {
  return ROLE_CONFIG[role]?.label || 'User';
}

export function getRoleColor(role) {
  return ROLE_CONFIG[role]?.color || '#546E7A';
}
